import {
  Activity,
  ArrowRight,
  BadgeCheck,
  Boxes,
  Check,
  ChevronRight,
  FileText,
  Globe,
  KeyRound,
  Layers,
  Link2,
  Lock,
  Network,
  Radar,
  Settings,
  ShieldCheck,
  Sparkles,
  Terminal,
  Unlock,
  Users,
} from "lucide-react";

import type {
  ScannerDiscovery,
  ScannerModule,
  ScannerPhase,
  ScannerTreeNode,
} from "./scanner.types";

import { cn } from "@/lib/utils";

type IconType = React.ComponentType<{ className?: string }>;

/** Pick an icon + tone for a page category (real crawl categories are free-form). */
export function categoryMeta(category: string): {
  icon: IconType;
  tone: string;
  label: string;
} {
  const c = (category || "other").toLowerCase();
  if (/(auth|login|signin|sign-in|account|session)/.test(c))
    return {
      icon: ShieldCheck,
      tone: "text-warning bg-warning/10 border-warning/20",
      label: titleCase(category),
    };
  if (/(admin|role|permission|user|member|team)/.test(c))
    return {
      icon: Users,
      tone: "text-info bg-info/10 border-info/20",
      label: titleCase(category),
    };
  if (/(setting|config|preference)/.test(c))
    return {
      icon: Settings,
      tone: "text-muted-foreground bg-muted border-border",
      label: titleCase(category),
    };
  if (/(form|create|edit|new|add)/.test(c))
    return {
      icon: FileText,
      tone: "text-violet-500 bg-violet-500/10 border-violet-500/20",
      label: titleCase(category),
    };
  if (/(api|endpoint|integration|webhook)/.test(c))
    return {
      icon: Terminal,
      tone: "text-rose-500 bg-rose-500/10 border-rose-500/20",
      label: titleCase(category),
    };
  if (/(dashboard|home|overview|report|analytic)/.test(c))
    return {
      icon: Activity,
      tone: "text-success bg-success/10 border-success/20",
      label: titleCase(category),
    };
  return {
    icon: Globe,
    tone: "text-sky-500 bg-sky-500/10 border-sky-500/20",
    label: titleCase(category),
  };
}

export function titleCase(s: string) {
  return (s || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
    .trim();
}

/** Pulsing "live" indicator dot. */
export function LiveDot() {
  return (
    <span className="relative flex h-2 w-2">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
    </span>
  );
}

const STEPS: { key: ScannerPhase; label: string; icon: IconType }[] = [
  { key: "connect", label: "Connect", icon: Link2 },
  { key: "auth", label: "Authenticate", icon: KeyRound },
  { key: "scanning", label: "Live scan", icon: Radar },
  { key: "complete", label: "Insights", icon: Sparkles },
];

/** Top progress stepper mirroring the reference's four-stage bar. */
export function ScannerStepper({ phase }: { phase: ScannerPhase }) {
  const activeIdx = STEPS.findIndex((s) => s.key === phase);
  return (
    <div className="hidden items-center gap-1 md:flex">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const state =
          i < activeIdx ? "done" : i === activeIdx ? "active" : "todo";
        return (
          <div key={s.key} className="flex items-center gap-1">
            <div
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                state === "active" && "border-info/40 bg-info/10 text-info",
                state === "done" &&
                  "border-success/30 bg-success/10 text-success",
                state === "todo" && "border-border text-muted-foreground",
              )}
            >
              {state === "done" ? (
                <Check className="h-3 w-3" />
              ) : (
                <Icon className="h-3 w-3" />
              )}
              {s.label}
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Compact stat tile used on the scanning hero. */
export function MiniStat({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: IconType;
}) {
  return (
    <div className="rounded-lg border border-border bg-card/80 p-3 backdrop-blur">
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/** Summary stat card used on the complete step. */
export function StatCard({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/** Selectable auth-strategy card (button, not a native radio). */
export function AuthStrategyCard({
  active,
  onClick,
  icon: Icon,
  title,
  desc,
}: {
  active: boolean;
  onClick: () => void;
  icon: IconType;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-2.5 rounded-lg border p-3 text-left transition-all",
        active
          ? "border-info/50 bg-info/5"
          : "border-border hover:bg-accent/40",
      )}
    >
      <span
        className={cn(
          "mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md",
          active ? "bg-info/10 text-info" : "bg-muted text-muted-foreground",
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-[13px] font-medium text-foreground">
          {title}
          {active && <Check className="h-3.5 w-3.5 text-info" />}
        </span>
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          {desc}
        </span>
      </span>
    </button>
  );
}

/** A single discovery timeline row. */
export function DiscoveryRow({ discovery }: { discovery: ScannerDiscovery }) {
  const meta = categoryMeta(discovery.category);
  const Icon = meta.icon;
  return (
    <li className="relative flex animate-fade-in gap-3 pb-3 last:pb-0">
      <div
        className={cn(
          "z-10 grid h-6 w-6 shrink-0 place-items-center rounded-full border",
          meta.tone,
        )}
      >
        <Icon className="h-3 w-3" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="rounded border border-border px-1.5 text-[10px] text-muted-foreground">
            {discovery.module || meta.label}
          </span>
          {discovery.isAuthProtected && (
            <span className="inline-flex items-center gap-1 text-[10px] text-warning">
              <Lock className="h-2.5 w-2.5" /> Protected
            </span>
          )}
        </div>
        <p
          className="mt-0.5 truncate font-mono text-[12px] text-foreground"
          title={discovery.routePath}
        >
          {discovery.routePath}
        </p>
      </div>
    </li>
  );
}

/** A detected-module (category cluster) card. */
export function ModuleRow({ module }: { module: ScannerModule }) {
  return (
    <div className="animate-fade-in rounded-lg border border-border bg-card p-3 transition-colors hover:bg-accent/30">
      <div className="flex items-center gap-2">
        <BadgeCheck className="h-4 w-4 text-success" />
        <div className="truncate text-[13px] font-medium">{module.name}</div>
        <span className="ml-auto text-[11px] font-medium text-muted-foreground tabular-nums">
          {module.pages} {module.pages === 1 ? "page" : "pages"}
        </span>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-info transition-all duration-700"
          style={{ width: `${Math.max(6, module.ratio)}%` }}
        />
      </div>
    </div>
  );
}

/** One branch of the live application map. */
export function TreeBranch({ node }: { node: ScannerTreeNode }) {
  const meta = categoryMeta(node.name);
  const Icon = meta.icon;
  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-2 text-[13px] font-medium">
        <Icon className="h-3.5 w-3.5 text-info" />
        <span className="truncate">{node.name}</span>
        <span className="ml-auto text-[10px] text-muted-foreground tabular-nums">
          {node.pages.length}
        </span>
      </div>
      {node.pages.length > 0 && (
        <ul className="mt-1 space-y-1 border-l border-border pl-3">
          {node.pages.slice(0, 8).map((p) => (
            <li
              key={p.routePath}
              className="flex items-center gap-2 truncate font-mono text-[11px] text-muted-foreground"
              title={p.routePath}
            >
              <span className="h-1 w-1 shrink-0 rounded-full bg-muted-foreground/40" />
              <span className="truncate">{p.routePath}</span>
              {p.isAuthProtected && (
                <Lock className="h-2.5 w-2.5 shrink-0 text-warning" />
              )}
            </li>
          ))}
          {node.pages.length > 8 && (
            <li className="pl-3 text-[10px] text-muted-foreground">
              +{node.pages.length - 8} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/** Small "what the AI will do" capability item. */
export function CapabilityItem({
  icon: Icon,
  text,
}: {
  icon: IconType;
  text: string;
}) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
      <div className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-info/10 text-info">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span>{text}</span>
      <Check className="ml-auto h-3.5 w-3.5 text-success" />
    </li>
  );
}

export const CAPABILITY_ICONS = {
  Boxes,
  Network,
  FileText,
  ShieldCheck,
  Sparkles,
  Globe,
  Lock,
  Unlock,
  ArrowRight,
  Layers,
};
